import { useTranslation } from 'react-i18next';
import type { Recipe } from '../../services/recipes/recipesService';
import { ALLERGIES, Allergy, toTranslationKey } from '../../types/RecipeEnums';
import { getUnmappedLegacyAllergens, isSupportedAllergenDetails } from '../../types/RecipeAllergens';
import { allergenDeclaration, hasIncompleteAllergenInformation } from './recipeAllergenPresentation';
import glutenIcon from '../../assets/allergens/gluten.svg';
import milkIcon from '../../assets/allergens/milk.svg';
import eggsIcon from '../../assets/allergens/eggs.svg';
import fishIcon from '../../assets/allergens/fish.svg';
import soyIcon from '../../assets/allergens/soy.svg';
import shellfishIcon from '../../assets/allergens/shellfish.svg';
import nutsIcon from '../../assets/allergens/nuts.svg';
import peanutsIcon from '../../assets/allergens/peanuts.svg';
import celeryIcon from '../../assets/allergens/celery.svg';
import mustardIcon from '../../assets/allergens/mustard.svg';
import sesameIcon from '../../assets/allergens/sesame.svg';
import sulphitesIcon from '../../assets/allergens/sulphites.svg';
import lupinIcon from '../../assets/allergens/lupin.svg';
import molluscsIcon from '../../assets/allergens/molluscs.svg';
import './recipeAllergenSummary.css';

// Reuse the recipe app's artwork, with the surrounding text colour in either theme.
const icons: Record<Allergy, string> = {
  'Gluten': glutenIcon,
  'Milk': milkIcon,
  'Eggs': eggsIcon,
  'Fish': fishIcon,
  'Soy': soyIcon,
  'Shellfish': shellfishIcon,
  'Tree Nuts': nutsIcon,
  'Peanuts': peanutsIcon,
  'Celery': celeryIcon,
  'Mustard': mustardIcon,
  'Sesame': sesameIcon,
  'Sulphites': sulphitesIcon,
  'Lupin': lupinIcon,
  'Molluscs': molluscsIcon
};

export function AllergenIcon({ allergen }: { allergen: Allergy }) {
  const mask = `url("${icons[allergen]}")`;
  return <span aria-hidden="true" className="recipe-allergen-icon" style={{ maskImage: mask, WebkitMaskImage: mask }} />;
}

export function AllergenInformationNotice() {
  const { t } = useTranslation();
  return <p role="note" className="recipe-allergen-notice">{t('recipeAllergens.display.incomplete')}</p>;
}

export function RecordedAllergens({ recipe, stale = false }: { recipe: Recipe; stale?: boolean }) {
  const { t } = useTranslation();
  const contains = ALLERGIES.filter(parent => allergenDeclaration(recipe, parent, stale).state === 'contains');
  const additional = getUnmappedLegacyAllergens(recipe.allergies);
  if (!contains.length && !additional.length) return <p className="recipe-allergen-muted">{t('recipeAllergens.display.noneRecorded')}</p>;
  return <ul className="recipe-allergen-badges" aria-label={t('recipeAllergens.presence.contains')}>
    {contains.map(parent => {
      const declaration = allergenDeclaration(recipe, parent, stale);
      return <li className="recipe-allergen-badge" key={parent}>
        <AllergenIcon allergen={parent} />
        <span>{t(`allergyKeys.${toTranslationKey(parent)}`)}
          {!!declaration.subtypes.length && <small>{declaration.subtypes.map(value => t(`recipeAllergens.subtypes.${value}`)).join(', ')}</small>}
        </span>
      </li>;
    })}
    {additional.map(value => <li className="recipe-allergen-badge" key={value}>{value}</li>)}
  </ul>;
}

export default function RecipeAllergenSummary({ recipe, stale = false, showNotice = true }: {
  recipe: Recipe; stale?: boolean; showNotice?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const incomplete = hasIncompleteAllergenInformation(recipe, stale);
  const absent = ALLERGIES.filter(parent => allergenDeclaration(recipe, parent, stale).state === 'absent');
  const reviewedAt = isSupportedAllergenDetails(recipe.allergenDetails) && recipe.allergenDetails.review.reviewedAt;
  const date = reviewedAt ? new Date(reviewedAt) : null;
  return <div className="recipe-allergen-summary">
    <RecordedAllergens recipe={recipe} stale={stale} />
    {!!absent.length && <details className="recipe-allergen-absence">
      <summary>{t('recipeAllergens.display.confirmedAbsent', { count: absent.length })}</summary>
      <p>{absent.map(parent => t(`allergyKeys.${toTranslationKey(parent)}`)).join(' · ')}</p>
    </details>}
    {!incomplete && date && !Number.isNaN(date.getTime()) && <p className="recipe-allergen-muted">
      {t('recipeAllergens.reviewedAt')}: {date.toLocaleString(i18n.language)}
    </p>}
    {showNotice && incomplete && <AllergenInformationNotice />}
  </div>;
}
